// Thin fetch wrapper — every fields API call lives behind a typed function
// that goes here. Keeps fetch() noise out of components; the fetch core (JSON
// in/out, typed ApiError, one-time 401 redirect hook) is shared with every
// other Region 13 SPA via @region13/ui (see admin-spa/src/lib/api.ts).

import { createApiClient, ApiError } from '@region13/ui'
import type {
  Me,
  Role,
  VenueSummary,
  VenueDetail,
  VenueCatalogUpdate,
  VenueCreateInput,
  VenueArchiveResult,
  LayoutUpdate,
  SubfieldInput,
  SubfieldUpdate,
  LayoutSubfieldsResponse,
} from '@region13/shared-types'

const API_BASE = '/api'

// Like referee.ayso13.org (email-code login), a 401 just means "not signed in
// yet", which routes/__root.tsx already renders (the login screen) from a
// plain `me.email === null` response. So no onAuthRedirect here — a 401
// should surface as a normal query error, not force-reload the page.
// `patch` is renamed to `patchReq` on import — a local `patch` argument name
// (see `updateLayout` below) would otherwise shadow the client method.
const { get, post, patch: patchReq, del } = createApiClient({ base: API_BASE })

/** Response shape of the `fields:manage` probe route (apps/fields/src/index.ts
 *  `GET /api/fields/summary`) — proves the authed round trip; a later task
 *  replaces this route + its consumer with the real field-management data. */
export interface FieldsSummary {
  email: string
  roles: Role[]
}

export const api = {
  // ── Auth (email-code) ─────────────────────────────────────────────────
  me: () => get<Me>('/auth/me'),
  requestCode: (email: string, turnstileToken: string | null) =>
    post<{ ok: boolean; dev_code?: string }>('/auth/request-code', {
      email,
      turnstile_token: turnstileToken,
    }),
  verifyCode: (email: string, code: string) => post<Me>('/auth/verify-code', { email, code }),
  logout: () => post<{ ok: boolean }>('/auth/logout', {}),

  // ── Fields (fields:manage) ────────────────────────────────────────────
  fieldsSummary: () => get<FieldsSummary>('/fields/summary'),

  // ── Spine data (fields:manage) ────────────────────────────────────────
  listVenues: () => get<{ venues: VenueSummary[] }>('/venues').then((d) => d.venues),
  venueDetail: (slug: string) => get<VenueDetail>(`/venues/${encodeURIComponent(slug)}`),
  /** Create a venue. 409 means the slug is taken; the dialog shows it inline. */
  createVenue: (input: VenueCreateInput) => post<{ slug: string }>('/venues', input),
  /** Edit the venue catalog row. `altsRefreshed` counts field_maps rows whose
   *  derived alt text a rename rewrote (hand-edited alts are left alone). */
  updateVenue: (slug: string, patch: VenueCatalogUpdate) =>
    patchReq<{ ok: boolean; altsRefreshed: number }>(`/venues/${encodeURIComponent(slug)}`, patch),
  /** Take a venue out of scheduling. `deactivated` is how many subfields it
   *  switched off, which the toast reports. */
  archiveVenue: (slug: string) =>
    post<VenueArchiveResult>(`/venues/${encodeURIComponent(slug)}/archive`, {}),
  restoreVenue: (slug: string) =>
    post<VenueArchiveResult>(`/venues/${encodeURIComponent(slug)}/restore`, {}),
  updateLayout: (id: string, patch: LayoutUpdate) =>
    patchReq<{ ok: boolean }>(`/layouts/${encodeURIComponent(id)}`, patch),
  deleteLayout: (id: string) => del<{ ok: boolean }>(`/layouts/${encodeURIComponent(id)}`),
  deleteVariant: (slug: string, variant: string) =>
    del<{ ok: boolean }>(`/venues/${encodeURIComponent(slug)}/variants/${encodeURIComponent(variant)}`),

  // ── Subfields (fields:manage) ─────────────────────────────────────────
  layoutSubfields: (layoutId: string) =>
    get<LayoutSubfieldsResponse>(`/layouts/${encodeURIComponent(layoutId)}/subfields`),
  createSubfield: (layoutId: string, input: SubfieldInput) =>
    post<{ id: string }>(`/layouts/${encodeURIComponent(layoutId)}/subfields`, input),
  updateSubfield: (id: string, patch: SubfieldUpdate) =>
    patchReq<{ ok: boolean }>(`/subfields/${encodeURIComponent(id)}`, patch),
  deleteSubfield: (id: string) => del<{ ok: boolean }>(`/subfields/${encodeURIComponent(id)}`),
  matchSubfield: (id: string, inleague_field_uid: string | null) =>
    patchReq<{ ok: boolean }>(`/subfields/${encodeURIComponent(id)}/match`, { inleague_field_uid }),
}

export { ApiError }
